<template>
  <CheckoutStep>
    <template #aside v-if="availableItems.length > 0">
      <CheckoutSummary
        :discount
        :discount-token
        :items="availableItems"
        :selected-payment-method="paymentMethod"
        :summary
        :swapOffer
        with-payment-availability
      >
        <template #title>
          {{ i18n(translations.cartSummaryTitle) }}
        </template>
      </CheckoutSummary>
    </template>

    <div aria-live="polite" role="status">
      <EmptyCart :items />

      <CartContent
        :is-loading="loading"
        :items
        :summary
        @next="handleNextStep"
        @update="emit('update', $event)"
      >
        <Swap
          :items
          :swap-offer
          :total-price-with-swap="summary.totalPriceAfterBuyback"
          zone="bouygues-in-store-cart"
          @updated="emit('update')"
        />
      </CartContent>
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed, onMounted, provide } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import CartContent from '~/scopes/checkout/components/Cart/components/CartContent/CartContent.vue'
import EmptyCart from '~/scopes/checkout/components/Cart/components/EmptyCart/EmptyCart.vue'
import Swap from '~/scopes/checkout/components/Cart/components/Swap/Swap.vue'
import CheckoutStep from '~/scopes/checkout/components/CheckoutStep/CheckoutStep.vue'
import CheckoutSummary from '~/scopes/checkout/components/CheckoutSummary/CheckoutSummary.vue'
import { useCheckoutTracking } from '~/scopes/checkout/composables/useCheckoutTracking'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'
import { getAvailableItems } from '~/scopes/checkout/utils/cart'

import translations from './CartStep.translations'

type CartItem = ApiSpecs['/bm/cart']['GET']['response']['cartItems'][number]
type CartSummary = ApiSpecs['/bm/cart']['GET']['response']['summary']

const props = defineProps<{
  items: CartItem[]
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  summary: CartSummary
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  loading: boolean
  paymentMethod: PaymentMethod | null
}>()

const emit = defineEmits<{
  (e: 'update', updatedItem?: CartItem[]): void
}>()

const i18n = useI18n()
const { trackFunnel } = useCheckoutTracking()

// We don't want user to be able to modify the cart from bouygues in-store experience
provide<boolean>('isCartLocked', true)

const availableItems = computed(() => getAvailableItems(props.items))

function handleNextStep() {
  navigateTo({
    name: CHECKOUT.IN_STORE_SALES.BOUYGUES_TELECOM.ADDRESSES,
  })
}

onMounted(async () => {
  trackFunnel(props.items, props.swapOffer, 'bouygues-in-store-cart')
})
</script>
